import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TablePagination,
  CircularProgress,
  Card,
  Tooltip,
  Chip
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import * as XLSX from 'xlsx';
import { useAuth } from "../../hooks/useAuth";
import EditIcon from '@mui/icons-material/Edit';
import UpdateTicketDialog from './UpdateTicketDialog';
import ViewIssueDialog from './ViewIssueDialog';
import { gql, useQuery, useLazyQuery } from '@apollo/client';

// Counters for the dashboard cards. `open` is the same predicate the list
// filter uses, so a card and the page it opens cannot disagree.
const GET_DEALER_TICKET_STATS = gql`
  query DealerTicketStats {
    dealerTicketStats {
      total
      open
      raised
      inProgress
      resolved
      ageing
      highPriority
    }
  }
`;

const GET_ALL_DEALER_TICKETS = gql`
  query GetAllDealerTickets($offset: Int, $limit: Int, $status: String, $search: String) {
    getAllDealerTickets(offset: $offset, limit: $limit, status: $status, search: $search) {
      totalCount
      offset
      limit
      data {
        id
        dealer_id
        vin
        ticket_number
        issue
        contact_email
        attached_filename_url
        attached_screenshot_url
        contact_name
        subject
        category
        priority
        status
        created_on
        updated_on
        comment
      }
    }
  }
`;

/**
 * The dashboard cards, in screen order.
 *
 * `key` binds a card to the stats field the server returns, and `filter` to the
 * status the list is asked for -- the same string on both sides, so a card
 * cannot count one thing and open another. "" means no filter (everything);
 * OPEN and AGEING are pseudo-statuses the backend resolves.
 */
const TICKET_CARDS: { key: string; label: string; filter: string; color: string }[] = [
  { key: 'total',        label: 'All tickets',   filter: '',            color: '#111' },
  { key: 'open',         label: 'Open',          filter: 'OPEN',        color: '#E60012' },
  { key: 'raised',       label: 'Raised',        filter: 'RAISED',      color: '#d97706' },
  { key: 'inProgress',   label: 'In progress',   filter: 'IN_PROGRESS', color: '#2563eb' },
  { key: 'resolved',     label: 'Resolved',      filter: 'RESOLVED',    color: '#16a34a' },
  // Open for more than 2 days. The queue that needs chasing, which is the
  // reason to have a dashboard rather than just a list.
  { key: 'ageing',       label: 'Open > 2 days', filter: 'AGEING',      color: '#b91c1c' },
];

const formatDate = (epochTimestamp: string | number | undefined | null) => {
  if (!epochTimestamp) return 'N/A';
  const timestamp = Number(epochTimestamp);
  if (isNaN(timestamp)) return String(epochTimestamp);
  const msTimestamp = timestamp < 1000000000000 ? timestamp * 1000 : timestamp;
  const date = new Date(msTimestamp);
  if (!isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  }
  return String(epochTimestamp);
};

const cellStyle = {
  fontSize: '14px',
  padding: '8px',
  width: 'auto',
  borderBottom: '1px solid #e0e0e0',
  borderRight: '1px solid #e0e0e0',
  color: '#000',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const TicketDetails = () => {
  const auth = useAuth();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [openViewIssueDialog, setOpenViewIssueDialog] = useState(false);
  const [viewIssueTicketData, setViewIssueTicketData] = useState<any>(null);

  // The cards double as the filter. "" is every ticket; OPEN and AGEING are
  // pseudo-statuses the backend resolves, the rest are literal statuses.
  const [statusFilter, setStatusFilter] = useState<string>("");

  const { data: statsData } = useQuery(GET_DEALER_TICKET_STATS, {
    fetchPolicy: "cache-and-network",
    // Swallowed: this query does not exist on a backend older than the ticket
    // dashboard, and a page that throws because its header is ahead of the
    // deploy is worse than a header reading "—".
    onError: () => {},
  });
  const stats = statsData?.dealerTicketStats;

  const { data: ticketsData, loading: queryLoading, error: queryError, refetch } = useQuery(GET_ALL_DEALER_TICKETS, {
    variables: {
      offset: page * rowsPerPage,
      limit: rowsPerPage,
      status: statusFilter || null,
      // Searched in the DATABASE, not in the page. The old client-side filter
      // only ever saw the current page, so searching a ticket number on page 1
      // silently missed it if it lived on page 3.
      search: searchTerm || null,
    },
  });

  const [exportTickets] = useLazyQuery(GET_ALL_DEALER_TICKETS, {
    fetchPolicy: 'network-only',
  });

  const [tableData, setTableData] = useState<any[]>([]);
  // Server-side now: the query above is already filtered and paged, so this is
  // just what came back. Filtering again here would hide rows the server
  // deliberately returned and make the total disagree with the table.
  const filteredData = tableData;


  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (ticketsData?.getAllDealerTickets) {
      const tickets = ticketsData.getAllDealerTickets.data || [];
      const formattedData = tickets.map((ticket: any, index: number) => ({
        srNo: (page * rowsPerPage) + index + 1,
        ticketNumber: ticket.ticket_number || 'N/A',
        category: ticket.category || 'N/A',
        vin: ticket.vin || 'N/A',
        customerName: ticket.contact_name || 'N/A',
        customerEmail: ticket.contact_email || 'N/A',
        subject: ticket.subject || 'N/A',
        issue: ticket.issue || 'N/A',
        ticketCreated: formatDate(ticket.created_on),
        ticketUpdated: formatDate(ticket.updated_on),
        status: ticket.status || 'N/A',
        priority: ticket.priority || 'N/A',
        ...ticket
      }));
      setTableData(formattedData);
      setTotalCount(ticketsData.getAllDealerTickets.totalCount || 0);
    }
  }, [ticketsData, page, rowsPerPage]);

  const handleViewIssueClick = (item: any) => {
    setViewIssueTicketData(item);
    setOpenViewIssueDialog(true);
  };

  const handleEditClick = (item: any) => {
    setSelectedTicket(item);
    setOpenUpdateDialog(true);
  };
  const handleExportToExcel = async () => {
    setLoading(true);
    try {
      const { data } = await exportTickets({
        variables: { offset: 0, limit: 10000 }
      });

      const allTickets = data?.getAllDealerTickets?.data || [];

      const formattedData = allTickets.map((ticket: any, index: number) => ({
        'Sr No.': index + 1,
        'Ticket Number': ticket.ticket_number || 'N/A',
        'Category': ticket.category || 'N/A',
        'VIN': ticket.vin || 'N/A',
        'Customer Name': ticket.contact_name || 'N/A',
        'Customer Email': ticket.contact_email || 'N/A',
        'Subject': ticket.subject || 'N/A',
        'Issue': ticket.issue || 'N/A',
        'Ticket Created': formatDate(ticket.created_on),
        'Updated On': formatDate(ticket.updated_on),
        'Status': ticket.status || 'N/A',
        'Priority': ticket.priority || 'N/A',
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Tickets');
      XLSX.writeFile(workbook, `All_Tickets_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export all tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const tableColumns = [
    "Sr No.",
    "Ticket Number",
    "Category",
    "VIN",
    "Customer Name",
    "Customer Email",
    "Subject",
    "Issue",
    "Ticket Created",
    "Updated On",
    "Status",
    "Priority",
    ...(auth.canUpdate("DEALER-TICKETS") || auth.canDelete("DEALER-TICKETS") ? ["Action"] : []),
  ];

  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      <Typography variant="h5" fontSize={{ xs: '1.5rem', sm: '2.125rem' }} sx={{ mb: 2, borderRadius: 2 }}>
        Ticket Details
      </Typography>

      {/* The dashboard. Each card IS the filter for the table below it, so the
          number on a card and the rows it opens are one predicate rather than
          two similar-looking queries. Counts come from the server unfiltered by
          search or paging, so they do not move while someone types. */}
      <Box sx={{ display: 'grid', gap: 1.5, mb: 2,
                 gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' } }}>
        {TICKET_CARDS.map((c) => {
          const active = statusFilter === c.filter;
          return (
            <Card
              key={c.label}
              onClick={() => setStatusFilter(c.filter)}
              sx={{
                p: 2, cursor: 'pointer', borderRadius: 2,
                border: active ? '2px solid #E60012' : '1px solid rgba(0,0,0,0.08)',
                transition: 'box-shadow .2s',
                '&:hover': { boxShadow: 3 },
              }}
            >
              <Typography variant="h5" fontWeight={700} sx={{ color: c.color }}>
                {stats ? (stats as Record<string, number>)[c.key] : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {c.label}
              </Typography>
            </Card>
          );
        })}
      </Box>
      <Card sx={{
        width: '100%',
        maxWidth: '100vw',
        mx: 'auto',
        boxShadow: 2,
        borderRadius: 3,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(100vh - 120px)' },
        bgcolor: '#fff',
        minHeight: 100,
      }}>
        {/* Header controls */}
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: '#fff',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          boxShadow: '0 2px 8px -4px #e0e0e0',
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 2,
          left: 0,
          right: 0,
          width: '100%',
          minWidth: 0,
          overflow: 'visible',
        }}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}
          >
            <Box
              display="flex"
              gap={1}
              alignItems="center"
              sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '100%', sm: '40%' } }}
            >
              <TextField
                size="small"
                placeholder="Search Ticket"
                variant="outlined"
                sx={{ minWidth: 120, background: '#f5f5f5', flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {/* <SearchIcon /> */}
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <IconButton
                sx={{ ml: 1, color: '#000' }}
                onClick={handleExportToExcel}
                disabled={loading}
              >
                <Tooltip title="Download ALL Tickets (Excel)" placement="top" arrow>
                  <CloudDownloadIcon />
                </Tooltip>
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Table area */}
        <Box sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 0.5, sm: 2 },
          py: 2,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                borderRadius: 2,
                width: '100%',
                minWidth: 800,
                background: '#fff',
                overflow: 'visible',
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 800, width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#E60012' }}>
                    {tableColumns.map((header) => (
                      <TableCell
                        key={header}
                        align="center"
                        sx={{
                          backgroundColor: '#E60012',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          whiteSpace: 'nowrap',
                          padding: { xs: '4px', sm: '6px' },
                          borderBottom: '1px solid #e0e0e0',
                          borderRight: '1px solid #e0e0e0',
                          position: 'sticky',
                          top: 0,
                          zIndex: 11,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queryLoading ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : queryError ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ fontSize: '1.1rem', color: '#ff0000', py: 4 }}>
                        Error fetching tickets!
                      </TableCell>
                    </TableRow>
                  ) : filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ fontSize: '1.1rem', color: '#888', py: 4 }}>
                        No records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.map((row, idx) => (
                      <TableRow
                        key={row.id || row.ticketNumber}
                        hover
                        sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5' }}
                      >
                        <TableCell align="center" sx={cellStyle}>{row.srNo}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.ticketNumber}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.category}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.vin}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.customerName}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.customerEmail}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.subject}</TableCell>
                        <TableCell align="center" sx={{ ...cellStyle, whiteSpace: 'normal' }}>
                          <Button
                            size="small"
                            onClick={() => handleViewIssueClick(row)}
                            sx={{
                              ml: 1,
                              minWidth: '80px',
                              fontSize: '12px',
                              backgroundColor: "#58B80F1A",
                              color: "#58B80F",
                              border: "1px solid #58B80F",
                              borderRadius: 10,
                            }}
                          >
                            View Issue
                          </Button>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.ticketCreated}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.ticketUpdated}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {row.status}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.priority}</TableCell>
                        {auth.canUpdate("DEALER-TICKETS") && (
                          <TableCell align="center" sx={cellStyle}>
                            <IconButton color="primary" onClick={() => handleEditClick(row)}>
                              <EditIcon sx={{ color: '#000000' }} />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* Pagination */}
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          background: '#fff',
          borderTop: '1px solid #e0e0e0',
          pt: 2,
          mt: 'auto',
          overflowX: 'auto',
        }}>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{
              minWidth: '600px',
              '.MuiTablePagination-toolbar': {
                flexWrap: 'nowrap',
                justifyContent: { xs: 'center', sm: 'space-between' },
              },
            }}
          />
        </Box>
      </Card>

      <UpdateTicketDialog
        open={openUpdateDialog}
        onClose={() => setOpenUpdateDialog(false)}
        ticketData={selectedTicket}
        onSubmit={(data) => {
          console.log('Update Ticket Submitted:', data);
          setOpenUpdateDialog(false);
          refetch();
        }}
      />
      <ViewIssueDialog
        open={openViewIssueDialog}
        onClose={() => setOpenViewIssueDialog(false)}
        ticketData={viewIssueTicketData}
      />
    </Box>
  );
};

export default TicketDetails;